import { format } from "date-fns";
import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import MainLayout from "@/components/layouts";
import { getInitials } from "@/lib/utils";
import { useIndividualDetail } from "./individual-detail.hooks";
import { Edit2, Mail, Phone, Trash, Info, Loader2 } from "lucide-react";
import DocumentUploadModal from "@/components/shared/upload-document/document-upload-modal";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import type { DocumentRecord } from "@shyne/shared/api";
import { DocumentOpenLink } from "@/components/shared/documents/document-open-link";
import { shownDay } from "@/lib/documents";
import { SkeletonContent } from "@/components/shared/skeleton-content";
import StatusBadge from "@/app/organization/settings/teammembers/teammembers.components/StatusBadge";
import { toUserStatus } from "@/repositories/organization-records";
import { useAppSelector, useAppDispatch } from "@/lib/store";
import { getServices } from "../services/services.slice";
import { Badge } from "@/components/ui/badge";
import { WorkerAvailability } from "@/components/shared/worker-availability";
import { DailyWorkHourlyRateCard } from "@/components/daily-work/DailyWorkHourlyRateCard";

export default function IndividualDetailPage() {
  const { individualId } = useParams<{ individualId: string }>();
  const {
    detail,
    loading,
    error,
    documents,
    documentsTotal,
    documentsPage,
    documentsPageSize,
    uploadModalOpen,
    uploadingDocument,
    updatingDocumentId,
    savingServices,
    serviceError,
    toggleService,
    uploadDocument,
    deleteDocument,
    updateDocumentExpiresAt,
    setUploadDocumentModalOpen,
    setDocPage,
    setDocPageSize,
  } = useIndividualDetail(individualId);

  const dispatch = useAppDispatch();
  const { list: servicesList } = useAppSelector((state) => state.services);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<DocumentRecord | null>(null);
  const [expiresAtValue, setExpiresAtValue] = useState("");

  const selectedServices = detail?.serviceIds ?? [];

  useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  if (loading || !detail) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Individuals", href: "/individuals" },
          { name: "Loading…", href: "#" },
        ]}
      >
        <SkeletonContent />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout
        breadcrumb={[
          { name: "Individuals", href: "/individuals" },
          { name: "Error", href: "#" },
        ]}
      >
        <div className="text-center py-8">
          <p className="text-destructive">{error}</p>
        </div>
      </MainLayout>
    );
  }

  const { contract } = detail;
  const { person } = contract;

  const breadcrumb = [
    { name: "Individuals", href: "/individuals" },
    { name: person.name, href: "#" },
  ];

  const openExpiresAtEditor = (document: DocumentRecord) => {
    setEditingDocument(document);
    setExpiresAtValue(document.expiresOn ?? "");
  };

  const closeExpiresAtEditor = () => {
    setEditingDocument(null);
    setExpiresAtValue("");
  };

  const handleSaveExpiresAt = async () => {
    if (!editingDocument) return;
    try {
      await updateDocumentExpiresAt(editingDocument.id, expiresAtValue || null);
      closeExpiresAtEditor();
    } catch {
      // Keep the dialog open; the hook already shows the error toast.
    }
  };

  const docColumns: ColumnDef<DocumentRecord>[] = [
    {
      header: "Document",
      accessor: (d) => <DocumentOpenLink document={d} />,
      cellClassName: "hover:bg-purple-50 dark:hover:bg-purple-900",
    },
    {
      header: "Uploaded",
      accessor: (d) => format(new Date(d.uploadedAt), "MM/dd/yyyy"),
    },
    {
      header: "Expires",
      accessor: (d) => shownDay(d.expiresOn),
    },
    {
      header: "Actions",
      accessor: (d) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" aria-label={`Edit expiration of ${d.filename}`} onClick={() => openExpiresAtEditor(d)} disabled={updatingDocumentId === d.id}>
            {updatingDocumentId === d.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Edit2 className="w-4 h-4" />
            )}
          </Button>
          <Dialog open={deletingDocId === d.id} onOpenChange={(open) => setDeletingDocId(open ? d.id : null)}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="icon" aria-label={`Delete ${d.filename}`}>
                <Trash className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove Document?</DialogTitle>
              </DialogHeader>
              <p className="pt-2 text-sm text-muted-foreground">
                Are you sure you want to delete "{d.filename}"? This action cannot be undone.
              </p>
              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setDeletingDocId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    await deleteDocument(d.id);
                    setDeletingDocId(null);
                    toast.success("Document deleted successfully.");
                  }}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      className: "whitespace-nowrap text-right",
    },
  ];

  const handleServiceToggle = (serviceId: string, checked: boolean) => {
    // Trigger the optimistic toggle (Redux handles the UI update immediately)
    toggleService(serviceId, checked);
  };

  // Get parent services only (no children)
  const parentServices = servicesList.filter((service) => !service.parent);

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="container mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* === Overview === */}
          <Card className="xl:col-span-3 border-none">
            <CardContent>
              <div className="flex items-center space-y-4 gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={person.avatar ?? ""} />
                  <AvatarFallback>{getInitials(person.name)}</AvatarFallback>
                </Avatar>
                <div className="w-full">
                  <div className="flex justify-start items-center w-full gap-8">
                    <h2 className="text-lg font-semibold">{person.name}</h2>
                    <StatusBadge status={toUserStatus(contract.status)} />
                  </div>
                  <p className="text-sm text-muted-foreground">{person.email}</p>
                  <p className="text-sm text-muted-foreground">{person.phone}</p>
                </div>
              </div>
              <div className="flex flex-col items-start text-center space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <a href={`tel:${person.phone ?? ""}`}>
                      <Phone className="w-4 h-4 mr-1" /> Call
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`mailto:${person.email ?? ""}`}>
                      <Mail className="w-4 h-4 mr-1" /> Email
                    </a>
                  </Button>
                  {/* <Button variant="outline" size="sm" disabled>
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit User
                  </Button> */}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="xl:col-span-3">
            <DailyWorkHourlyRateCard
              payer={contract.employer.id}
              payee={{ worker: person.id }}
              services={parentServices.map((service) => ({ id: service.id, name: service.name }))}
              testIdPrefix={`daily-work-rate-individual-${contract.id}`}
            />
          </div>

          {/* === Documents === */}
          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle>
                <div className="flex items-center">
                  <p className="mr-auto uppercase">Documents</p>
                  <Button onClick={() => setUploadDocumentModalOpen(true)}>Upload Document</Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <SimpleTable<DocumentRecord>
                  columns={docColumns}
                  data={documents}
                  totalCount={documentsTotal}
                  page={documentsPage}
                  pageSize={documentsPageSize}
                  setPage={setDocPage}
                  setPageSize={setDocPageSize}
                  sortKey={undefined}
                  sortOrder="asc"
                  onSortChange={() => {}}
                  filters={[]}
                  onReset={() => {}}
                  onApply={() => {}}
                />
              ) : (
                <p className="text-center text-muted-foreground py-6">No documents.</p>
              )}
            </CardContent>
          </Card>

          {/* === Services === */}
          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle className="uppercase">Services</CardTitle>
              {serviceError && <p className="text-sm text-destructive mt-2">{serviceError}</p>}
            </CardHeader>
            <CardContent>
              {parentServices.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {parentServices.map((service) => {
                    const isSaving = savingServices.includes(service.id);
                    return (
                      <div key={service.id} className={`flex items-center space-x-2 ${isSaving ? "opacity-75" : ""}`}>
                        <Checkbox
                          id={service.id}
                          checked={selectedServices.includes(service.id)}
                          disabled={isSaving}
                          onCheckedChange={(checked) => handleServiceToggle(service.id, checked as boolean)}
                        />
                        <label
                          htmlFor={service.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                        >
                          {service.name}
                          {isSaving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                        </label>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-6">No services available.</p>
              )}
            </CardContent>
          </Card>

          {/* === Schedule & Overview === */}
          <Card className="xl:col-span-3">
            <CardHeader>
              <CardTitle className="uppercase">Schedule & Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <WorkerAvailability contractId={contract.id} workerName={person.name} />
            </CardContent>
          </Card>

          <DocumentUploadModal
            open={uploadModalOpen}
            onOpenChange={setUploadDocumentModalOpen}
            onUpload={async (file, expiresAt) => {
              await uploadDocument(file, expiresAt);
            }}
            uploading={uploadingDocument}
          />
          <Dialog open={!!editingDocument} onOpenChange={(open) => (!open ? closeExpiresAtEditor() : undefined)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Expiration Date</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="individual-document-expires-at">Expires At</Label>
                <Input
                  id="individual-document-expires-at"
                  type="date"
                  value={expiresAtValue}
                  onChange={(event) => setExpiresAtValue(event.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeExpiresAtEditor}>
                  Cancel
                </Button>
                <Button variant="outline" onClick={() => setExpiresAtValue("")}>
                  Clear Date
                </Button>
                <Button onClick={handleSaveExpiresAt} disabled={updatingDocumentId === editingDocument?.id}>
                  {updatingDocumentId === editingDocument?.id && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </MainLayout>
  );
}
